/**
 * Util for expiring and refreshing Angular signal-based state slices
 * after a configurable TTL (default: 24 hours).
 *
 *  - On every `markLoaded(key)` call, the current epoch ms is written to localStorage.
 *  - On `isExpired(key)`, the stored timestamp is compared against `Date.now()`.
 *  - `clearMark(key)` / `clearAll()` let you force-expire slices programmatically.
 *
 *
 *   To clear all TTLs:
 *   this.expiry.clearAll();
 */

export interface StateExpiryOptions {
  ttlMs?: number;
  /**
   * localStorage key prefix so multiple apps / feature modules
   */
  storagePrefix: string;
}

const DEFAULT_STATE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export class StateExpiryUtil {
  private readonly ttlMs: number;
  private readonly prefix: string;

  /** Keys marked during this session (for in-memory fast-path). */
  private readonly sessionMarks = new Map<string, number>();

  constructor(options: StateExpiryOptions) {
    this.ttlMs = options.ttlMs ?? DEFAULT_STATE_TTL_MS;
    this.prefix = options.storagePrefix;
  }

  /**
   * Record that a state slice was successfully loaded right now.
   * Call this inside a `tap()` after a successful API response.
   */
  markLoaded(sliceKey: string): void {
    const now = Date.now();
    this.sessionMarks.set(sliceKey, now);
    this.writeToStorage(sliceKey, now);
  }

  /**
   * Returns `true` when the slice has never been loaded,
   * or its mark is older than `ttlMs`.
   */
  isExpired(sliceKey: string): boolean {
    const ts = this.getTimestamp(sliceKey);
    if (ts === null) return true;
    return Date.now() - ts > this.ttlMs;
  }

  /**
   * Returns the remaining TTL in milliseconds for a slice,
   * or `0` if already expired / never loaded.
   */
  remainingTtlMs(sliceKey: string): number {
    const ts = this.getTimestamp(sliceKey);
    if (ts === null) return 0;
    return Math.max(0, this.ttlMs - (Date.now() - ts));
  }

  /**
   * Force-expires a single slice (e.g. after a mutation that
   * requires a fresh fetch on next visit).
   */
  clearMark(sliceKey: string): void {
    this.sessionMarks.delete(sliceKey);
    this.removeFromStorage(sliceKey);
  }

  /** Force-expires ALL tracked slices (e.g. on logout or store switch). */
  clearAll(): void {
    this.sessionMarks.clear();
    this.removeAllFromStorage();
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  private storageKey(sliceKey: string): string {
    return `${this.prefix}:${sliceKey}`;
  }

  /** In-memory first, then localStorage. */
  private getTimestamp(sliceKey: string): number | null {
    const timestamp = this.sessionMarks.get(sliceKey);
    if (timestamp !== undefined) {
      return timestamp;
    }
    return this.readFromStorage(sliceKey);
  }

  private writeToStorage(sliceKey: string, ts: number): void {
    try {
      localStorage.setItem(this.storageKey(sliceKey), String(ts));
    } catch {
      // Quota exceeded or private-mode restriction
    }
  }

  private readFromStorage(sliceKey: string): number | null {
    try {
      const raw = localStorage.getItem(this.storageKey(sliceKey));
      if (raw === null) return null;
      const ts = Number(raw);
      return Number.isNaN(ts) ? null : ts;
    } catch {
      return null;
    }
  }

  private removeFromStorage(sliceKey: string): void {
    try {
      localStorage.removeItem(this.storageKey(sliceKey));
    } catch { /* ignore */ }
  }

  private removeAllFromStorage(): void {
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith(`${this.prefix}:`))
        .forEach(k => localStorage.removeItem(k));
    } catch { /* ignore */ }
  }
}
